import React, { useEffect, useMemo, useRef, useState } from "react";
import PitchCurveAudioPanel from "./PitchCurveAudioPanel";

const COLOR = {
  green: {
    label: "Good",
    short: "G",
    text: "text-slate-900",
    bg: "bg-green-50",
    border: "border-green-300",
    pill: "bg-green-50 text-green-700 border-green-300",
    dot: "bg-green-500",
    mark: "bg-transparent text-slate-900",
    surface: "from-white via-green-50/70 to-white",
  },
  yellow: {
    label: "Almost",
    short: "Y",
    text: "text-yellow-800",
    bg: "bg-yellow-50",
    border: "border-yellow-300",
    pill: "bg-yellow-50 text-yellow-800 border-yellow-300",
    dot: "bg-yellow-400",
    mark: "bg-yellow-100 text-yellow-900 ring-1 ring-yellow-300",
    surface: "from-white via-yellow-50/80 to-white",
  },
  red: {
    label: "Needs work",
    short: "R",
    text: "text-red-700",
    bg: "bg-red-50",
    border: "border-red-300",
    pill: "bg-red-50 text-red-700 border-red-300",
    dot: "bg-red-500",
    mark: "bg-red-100 text-red-800 ring-1 ring-red-300",
    surface: "from-white via-red-50/80 to-white",
  },
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function colorOf(value) {
  return COLOR[value] || COLOR.green;
}

function getAudioFetchUrl(audioUrl) {
  if (!audioUrl) return null;
  if (audioUrl.startsWith("http")) return encodeURI(audioUrl);

  return encodeURI(`${process.env.REACT_APP_USER_API_URL}${audioUrl}`);
}

function getBetterMeAudio(reply) {
  return (
    reply?.betterMe ||
    reply?.better_me ||
    reply?.betterMeAudio ||
    reply?.better_me_audio ||
    reply?.audio?.betterMe ||
    reply?.audio?.better_me ||
    (reply?.audio?.kind === "better_me_final" ? reply.audio : null) ||
    null
  );
}

function getBetterMeIntonation(betterMe) {
  return (
    betterMe?.intonation ||
    betterMe?.assessment?.intonation ||
    betterMe?.analysis?.intonation ||
    betterMe?.analysis ||
    null
  );
}

function countChanges(reply) {
  let words = 0;
  let letters = 0;

  for (const word of reply?.ratings || []) {
    if (word.rating && word.rating !== "green") words += 1;

    for (const letter of word.letters || []) {
      if (letter.rating && letter.rating !== "green") letters += 1;
    }
  }

  const sentenceChanged =
    reply?.sentenceRating && reply.sentenceRating !== "green" ? 1 : 0;

  return {
    sentenceChanged,
    words,
    letters,
    total: sentenceChanged + words + letters,
  };
}

function getNote(reply) {
  const note = reply?.content || reply?.text || reply?.note || "";

  if (!note || note === "Human pronunciation verdict") return "";

  return String(note).trim();
}

function getChapterDisplay(reply) {
  return reply?.chapterTitle || reply?.file || "";
}

function SummaryPill({ sentenceRating, changes, chapterDisplay, hasBetterMe }) {
  const meta = colorOf(sentenceRating);

  const label =
    changes.total === 0
      ? "All green"
      : `${changes.total} mark${changes.total === 1 ? "" : "s"}`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={cx(
          "rounded-full border px-3 py-1 text-xs font-black",
          meta.pill
        )}
      >
        {meta.label}
      </span>

      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700">
        {label}
      </span>

      {hasBetterMe && (
        <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-black text-violet-800">
          Better Me
        </span>
      )}

      {chapterDisplay && (
        <span className="max-w-[220px] truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-500">
          {chapterDisplay}
        </span>
      )}
    </div>
  );
}

function DotStrip({ ratings }) {
  if (!ratings?.length) return null;

  return (
    <div className="mt-3 flex max-w-full flex-wrap items-center gap-1">
      {ratings.map((word, index) => {
        const meta = colorOf(word?.rating || "green");

        return (
          <span
            key={`${word?.index ?? index}-${word?.word || "word"}`}
            title={`${word?.word || "Word"}: ${meta.label}`}
            className={cx("h-2 w-2 rounded-full", meta.dot)}
          />
        );
      })}
    </div>
  );
}

function BetterMePlayButton({
  betterMe,
  isPlaying,
  isLoading,
  error,
  onPlay,
  compact = false,
}) {
  if (!betterMe?.audio_url) return null;

  const playStyle = isPlaying
    ? {
        backgroundColor: "#6d28d9",
        borderColor: "#4c1d95",
        color: "#ffffff",
        boxShadow: "0 14px 28px rgba(109, 40, 217, 0.28)",
      }
    : {
        backgroundColor: "#f5f3ff",
        borderColor: "#c4b5fd",
        color: "#5b21b6",
        boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
      };

  return (
    <div className={cx("min-w-0", compact ? "mt-2" : "mt-0")}>
      <button
        type="button"
        onClick={onPlay}
        disabled={isLoading}
        className={cx(
          "inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border-2 px-4 py-2 text-sm font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60",
          compact && "w-full sm:w-auto"
        )}
        style={playStyle}
      >
        <span>{isLoading ? "…" : isPlaying ? "■" : "▶"}</span>
        <span>
          {isLoading
            ? "Loading Better Me"
            : isPlaying
              ? "Stop Better Me"
              : "Play Better Me"}
        </span>
      </button>

      {error && (
        <div className="mt-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}

function BetterMeCurvePanel({
  betterMe,
  audioRef,
  isPlaying,
  show,
  onShowChange,
}) {
  const intonation = getBetterMeIntonation(betterMe);

  if (!betterMe?.audio_url || !intonation || !show) return null;

  return (
    <div className="mt-4 overflow-hidden rounded-[22px] border border-violet-100 bg-white shadow-sm">
      <PitchCurveAudioPanel
        key={`${betterMe?.audio_id || betterMe?.audio_url || "better-me"}-${
          isPlaying ? "playing" : "idle"
        }`}
        assessment={null}
        intonationOverride={intonation}
        audioRef={audioRef}
        speed={1}
        expanded={show}
        onExpandedChange={onShowChange}
        title="Better Me pitch curve"
        subtitle={
          isPlaying
            ? "Playing the edited learner voice."
            : "Pitch contour attached to this human verdict."
        }
        compact
      />
    </div>
  );
}

function AnnotatedWord({ word }) {
  const wordRating = word?.rating || "green";
  const wordMeta = colorOf(wordRating);
  const letters = word?.letters || [];
  const hasMarkedLetter = letters.some(
    (letter) => letter?.rating && letter.rating !== "green"
  );
  const wordChanged = wordRating !== "green";

  return (
    <span
      className={cx(
        "inline whitespace-nowrap rounded-md px-0.5 py-0.5 font-semibold",
        wordChanged && !hasMarkedLetter
          ? cx(wordMeta.bg, wordMeta.text)
          : "text-slate-900"
      )}
      title={`${word?.word || "Word"}: ${wordMeta.label}`}
    >
      {letters.length > 0
        ? letters.map((letter, index) => {
            const rating = letter?.rating || "green";
            const meta = colorOf(rating);
            const changed = rating !== "green";

            return (
              <span
                key={`${letter?.index ?? index}-${letter?.char || ""}`}
                className={cx(
                  "inline rounded-[4px]",
                  changed
                    ? cx("px-[2px] py-[1px] font-black", meta.mark)
                    : "font-semibold text-slate-900"
                )}
                title={`${letter?.char || ""}: ${meta.label}`}
              >
                {letter?.char}
              </span>
            );
          })
        : word?.word}
    </span>
  );
}

function AnnotatedSentence({ ratings }) {
  if (!ratings?.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm font-bold text-slate-400">
        No detailed rating available.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
      <p className="max-w-5xl text-[17px] font-medium leading-9 tracking-normal text-slate-900">
        {ratings.map((word, index) => (
          <React.Fragment
            key={`${word?.index ?? index}-${word?.word || "word"}`}
          >
            <AnnotatedWord word={word} />
            {index < ratings.length - 1 ? " " : ""}
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500">
      <span className="mr-1 text-slate-400">Legend:</span>

      <span className="rounded-full border border-green-300 bg-green-50 px-2 py-1 text-green-700">
        Good
      </span>

      <span className="rounded-full border border-yellow-300 bg-yellow-50 px-2 py-1 text-yellow-800">
        Almost
      </span>

      <span className="rounded-full border border-red-300 bg-red-50 px-2 py-1 text-red-700">
        Needs work
      </span>
    </div>
  );
}

export default function RateMeReplyBlock({ reply }) {
  const [open, setOpen] = useState(false);
  const [isPlayingBetterMe, setIsPlayingBetterMe] = useState(false);
  const [loadingBetterMe, setLoadingBetterMe] = useState(false);
  const [betterMeError, setBetterMeError] = useState("");
  const [showBetterMeCurve, setShowBetterMeCurve] = useState(false);

  const betterMeAudioRef = useRef(null);

  const sentenceRating = reply?.sentenceRating || "green";
  const sentenceMeta = colorOf(sentenceRating);
  const ratings = reply?.ratings || [];
  const note = getNote(reply);
  const chapterDisplay = getChapterDisplay(reply);
  const betterMe = getBetterMeAudio(reply);
  const betterMeIntonation = getBetterMeIntonation(betterMe);
  const hasBetterMe = Boolean(betterMe?.audio_url);
  const hasBetterMeCurve = Boolean(betterMeIntonation);

  const changes = useMemo(() => countChanges(reply), [reply]);

  useEffect(() => {
    return () => {
      if (betterMeAudioRef.current) {
        betterMeAudioRef.current.pause();

        if (betterMeAudioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(betterMeAudioRef.current.src);
        }

        betterMeAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setIsPlayingBetterMe(false);
    setLoadingBetterMe(false);
    setBetterMeError("");
    setShowBetterMeCurve(false);

    if (betterMeAudioRef.current) {
      betterMeAudioRef.current.pause();

      if (betterMeAudioRef.current.src?.startsWith("blob:")) {
        URL.revokeObjectURL(betterMeAudioRef.current.src);
      }

      betterMeAudioRef.current = null;
    }
  }, [reply?._id, reply?.id]);

  const stopBetterMe = () => {
    if (!betterMeAudioRef.current) {
      setIsPlayingBetterMe(false);
      return;
    }

    betterMeAudioRef.current.pause();
    betterMeAudioRef.current.currentTime = 0;

    if (betterMeAudioRef.current.src?.startsWith("blob:")) {
      URL.revokeObjectURL(betterMeAudioRef.current.src);
    }

    betterMeAudioRef.current = null;
    setIsPlayingBetterMe(false);
  };

  const playBetterMe = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (isPlayingBetterMe) {
      stopBetterMe();
      return;
    }

    const fetchUrl = getAudioFetchUrl(betterMe?.audio_url);

    if (!fetchUrl) {
      setBetterMeError("No Better Me audio was found.");
      return;
    }

    setLoadingBetterMe(true);
    setBetterMeError("");

    try {
      stopBetterMe();

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await fetch(fetchUrl, { headers });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) {
        throw new Error(`Better Me audio ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const player = new Audio(objectUrl);

      betterMeAudioRef.current = player;

      player.onplay = () => {
        setIsPlayingBetterMe(true);
        setOpen(true);

        if (hasBetterMeCurve) {
          setShowBetterMeCurve(true);
        }
      };

      player.onpause = () => {
        setIsPlayingBetterMe(false);
      };

      player.onended = () => {
        URL.revokeObjectURL(objectUrl);
        betterMeAudioRef.current = null;
        setIsPlayingBetterMe(false);
      };

      player.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        betterMeAudioRef.current = null;
        setIsPlayingBetterMe(false);
        setBetterMeError("Could not play Better Me audio.");
      };

      await player.play();
    } catch (error) {
      console.error("play Better Me in reply failed:", error);

      setIsPlayingBetterMe(false);
      setBetterMeError(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to listen to Better Me."
          : "Could not play Better Me audio."
      );
    } finally {
      setLoadingBetterMe(false);
    }
  };

  const toggleOpen = () => {
    setOpen((value) => !value);
  };

  return (
    <div
      className={cx(
        "mt-4 overflow-hidden rounded-[26px] border bg-gradient-to-br shadow-sm transition",
        sentenceMeta.border,
        sentenceMeta.surface
      )}
    >
      <div
        className={cx(
          "flex w-full items-start justify-between gap-3 px-3 py-3 text-left sm:gap-4 sm:px-4",
          sentenceRating === "green" ? "bg-white/85" : sentenceMeta.bg
        )}
      >
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={toggleOpen}
            className="block w-full rounded-2xl px-2 py-1 text-left transition hover:bg-white/75 focus:outline-none focus:ring-4 focus:ring-slate-200"
          >
            <SummaryPill
              sentenceRating={sentenceRating}
              changes={changes}
              chapterDisplay={chapterDisplay}
              hasBetterMe={hasBetterMe}
            />

            {!open && <DotStrip ratings={ratings} />}

            {!open && note && (
              <div className="mt-3 line-clamp-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold leading-6 text-slate-700">
                “{note}”
              </div>
            )}
          </button>

          {!open && (
            <BetterMePlayButton
              betterMe={betterMe}
              isPlaying={isPlayingBetterMe}
              isLoading={loadingBetterMe}
              error={betterMeError}
              onPlay={playBetterMe}
              compact
            />
          )}
        </div>

        <button
          type="button"
          onClick={toggleOpen}
          className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
        >
          {open ? "Hide" : "Details"}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-4 sm:px-4">
          <AnnotatedSentence ratings={ratings} />
          <Legend />

          {hasBetterMe && (
            <div className="mt-4 rounded-[22px] border border-violet-100 bg-white px-3 py-3 shadow-sm sm:px-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.14em] text-violet-500">
                    Better Me
                  </div>

                  <div className="mt-1 text-sm font-black text-slate-950">
                    Edited learner voice
                  </div>

                  <div className="mt-0.5 text-xs font-semibold text-slate-500">
                    {hasBetterMeCurve
                      ? "Play it and follow the pitch curve below."
                      : "Audio is attached. No pitch curve was stored with this reply."}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                  <BetterMePlayButton
                    betterMe={betterMe}
                    isPlaying={isPlayingBetterMe}
                    isLoading={loadingBetterMe}
                    error={betterMeError}
                    onPlay={playBetterMe}
                  />

                  {hasBetterMeCurve && !showBetterMeCurve && !isPlayingBetterMe && (
                    <button
                      type="button"
                      onClick={() => setShowBetterMeCurve(true)}
                      className="inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl border border-violet-200 bg-white px-4 py-2 text-xs font-black text-violet-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-violet-50"
                    >
                      <span>⌁</span>
                      <span>Inspect curve</span>
                    </button>
                  )}
                </div>
              </div>

              <BetterMeCurvePanel
                betterMe={betterMe}
                audioRef={betterMeAudioRef}
                isPlaying={isPlayingBetterMe}
                show={showBetterMeCurve || isPlayingBetterMe}
                onShowChange={setShowBetterMeCurve}
              />
            </div>
          )}

          {note && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Note
              </div>

              <p className="mt-1 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-800">
                {note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}